import { useEffect, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import type {
  UnitSpacingConfig,
  UnitSpacingConfigsResponse,
} from 'types/api-responses';
import { geometryRefreshAtom } from './atoms';
import {
  tcInventoryGunBarrelScopeAtom,
  tcSelectedDevAreaAtom,
  tcUnitSpacingConfigsAtom,
} from './type-curve-filters/atoms';

// Stable reference so consumers' memos don't recompute every render when empty.
const EMPTY: UnitSpacingConfig[] = [];

/**
 * The scoped unit's per-formation spacing configs
 * (`GET …/units/:unitId/spacing_configs`) — one row per zone. Fetched once for
 * the inventory gun barrel (which both the main page and the Unit Builder mount)
 * and published to `tcUnitSpacingConfigsAtom` so the builder's sibling spacing
 * controls can seed the selected formation's values without re-fetching.
 *
 * Inactive off an inventory surface (`tcInventoryGunBarrelScopeAtom` null) or on
 * a draft/clone unit (no real, positive development-area id) — returns `[]`.
 *
 * @return {UnitSpacingConfig[]} The zone rows, or `[]` when unavailable.
 */
export function useUnitSpacingConfigs(): UnitSpacingConfig[] {
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const setConfigs = useSetAtom(tcUnitSpacingConfigsAtom);

  const workspaceId = inventoryScope?.inventoryWorkspaceId ?? null;
  // Real (persisted) units only — draft/clone ids are null or synthetic negatives.
  const unitId =
    selectedDevArea && selectedDevArea.developmentAreaId > 0
      ? selectedDevArea.developmentAreaId
      : null;

  const resourceAtom = useMemo(
    () =>
      createResourceAtom<UnitSpacingConfigsResponse>(
        workspaceId && unitId
          ? `/inventory_workspaces/${workspaceId}/units/${unitId}/spacing_configs`
          : undefined,
        'keep-old-data-on-refetch'
      ),
    [workspaceId, unitId]
  );
  const [resource, refetch] = useAtom(resourceAtom);
  const { data } = formatResource(resource);

  // Re-fetch after EITHER inventory action: both Update Geometries and Generate
  // LTD re-resolve the unit's per-zone spacing server-side, so the resolved config
  // changes. `geometryRefreshAtom` bumps on either. Only refetch on an actual bump
  // — the atom already fetches on mount and on unit change, so this must not double
  // up there. One shared fetch updates both surfaces' gun barrels (they read the
  // shared `tcUnitSpacingConfigsAtom`).
  const geometryRefresh = useAtomValue(geometryRefreshAtom);
  const prevTrigger = useRef(geometryRefresh);
  useEffect(() => {
    if (prevTrigger.current === geometryRefresh) return;
    prevTrigger.current = geometryRefresh;
    refetch();
  }, [geometryRefresh, refetch]);

  // Gate on ids so a stale previous-unit response can't leak when there is no
  // current unit (the inert atom keeps its old data).
  const configs = workspaceId && unitId ? (data?.spacingConfigs ?? null) : null;

  useEffect(() => {
    setConfigs(configs);
    return () => setConfigs(null);
  }, [configs, setConfigs]);

  return configs ?? EMPTY;
}
